/*
 * $Id:: CalendarWrapper.js 2021/02/26 06:09 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useState, useEffect } from 'react';

import { makeStyles, useTheme } from '@material-ui/core/styles';
import { DateRange } from 'react-date-range';
import * as rdrLocales from 'react-date-range/dist/locale';

import { ReactComponent as RangeDateIcon } from '../../assets/images/date-range-icon.svg';
import { getLocale, t } from '../../providers';
import { bareDateTimeFormat } from '../../providers/DateFormatProvider';

const useStyles = makeStyles((theme) => ({
    datepickerContainer: {
        backgroundColor: 'white',
        boxShadow: '0px 6px 10px rgba(0, 0, 0, 0.04), 0px 1px 18px rgba(0, 0, 0, 0.02), 0px 3px 5px rgba(0, 0, 0, 0.1)',
        borderRadius: '4px',
        display: 'flex',
        flexFlow: 'column nowrap',
    },
    // Overriding default theme of datepicker.
    datepicker: {
        '& .rdrMonth': {
            width: '21em',
            paddingLeft: '1rem',
            paddingRight: '1rem',
            '& .rdrDayNumber': {
                fontSize: '11.5px',
                fontWeight: '600',
            },
            '& .rdrStartEdge.rdrEndEdge, .rdrStartEdge, .rdrEndEdge, .rdrDayEndOfMonth .rdrInRange, .rdrDayEndOfMonth .rdrStartEdge, .rdrDayEndOfWeek .rdrInRange, .rdrDayEndOfWeek .rdrStartEdge, .rdrDayStartOfMonth .rdrInRange, .rdrDayStartOfWeek .rdrInRange': {
                borderRadius: '50%',
                height: '70%',
                width: '70%',
                top: '50%',
                left: '50%',
                transform: 'translate(-48%, -50%)',
            },
            '& .rdrInRange': {
                borderRadius: '50%',
                height: '70%',
                width: '70%',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: '#DFEFFF',
                color: theme.palette.text.mainBlue,
                '& + span': {
                    '& span': {},
                },
            },
            '& .rdrDay:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span, .rdrDay:not(.rdrDayPassive)': {
                color: theme.palette.text.mainBlue,
            },
            '& .rdrDayToday .rdrDayNumber span:after': {
                width: 0,
            },
            '& .rdrMonthName': {
                display: 'none',
            },
        },
    },
    datepickerHeader: {
        display: 'flex',
        flexFlow: 'row nowrap',
    },
    datepickerLeftArrow: {
        width: '20px',
        paddingLeft: '1rem',
        display: 'flex',
        flexFlow: 'row wrap',
        justifyContent: 'space-between',
    },
    datepickerRightArrow: {
        width: '20px',
        paddingRight: '1rem',
        display: 'flex',
        flexFlow: 'row wrap',
        justifyContent: 'space-between',
    },
    datepickerPreviewDates: {
        borderTop: '1px solid rgba(224, 224, 224, 1)',
        display: 'flex',
        flexFlow: 'row nowrap',
    },
    datepickerPreviewDate: {
        width: '50%',
        padding: '1rem',
        display: 'flex',
        flexFlow: 'row wrap',
        alignItems: 'center',
        color: '#616161',
        fontSize: '13px',
    },
    rangeDateIcon: {
        height: '1rem',
        paddingTop: '1px',
        marginRight: '0.8rem',
        marginLeft: '0.5rem',
        '& svg': {
            width: '1rem',
            height: '1rem',
        },
    },
    datepickerSelectedDate: {
        backgroundColor: '#F1F2F5',
        borderRadius: '4px',
        width: '5.5rem',
        height: '2rem',
        marginLeft: '0.6rem',
        textAlign: 'center',
        verticalAlign: 'middle',
        lineHeight: '2rem',
    },
}));

const CalendarWrapper = ({ onUpdateRanges, range, customHeader }) => {
    const classes = useStyles();
    const theme = useTheme();
    const locale = getLocale(); // get current language

    const [dateRange, setDateRange] = useState([{}]);

    useEffect(() => {
        const now = new Date();

        const { startDate, endDate } = range || {};
        const newDateRange = [{ key: 'selection', startDate: now, endDate: now }];
        if (startDate) newDateRange[0].startDate = startDate;
        if (endDate) newDateRange[0].endDate = endDate;
        setDateRange(newDateRange);
    }, [range, setDateRange]);

    const onChangeRanges = (item) => {
        onUpdateRanges({ startDate: item.selection.startDate, endDate: item.selection.endDate });
    };

    return (
        <div className={classes.datepickerContainer}>
            {!!customHeader ? customHeader : null}
            <DateRange
                className={classes.datepicker}
                editableDateInputs={true}
                onChange={onChangeRanges}
                moveRangeOnFirstSelection={false}
                showSelectionPreview={false}
                showMonthAndYearPickers={true}
                showDateDisplay={false}
                color={theme.palette.text.mainBlue}
                rangeColors={[theme.palette.text.mainBlue]}
                ranges={dateRange}
                months={2}
                direction={'horizontal'}
                showMonthName={true}
                showPreview={false}
                monthDisplayFormat={'MMMM yyyy'}
                locale={rdrLocales[locale ? locale : 'en']}
            />
            <div className={classes.datepickerPreviewDates}>
                <div className={classes.datepickerPreviewDate}>
                    <div className={classes.rangeDateIcon}>
                        <RangeDateIcon />
                    </div>
                    <span>{t('from.date')}</span>
                    <div className={classes.datepickerSelectedDate}>
                        {!!dateRange[0].startDate ? bareDateTimeFormat(dateRange[0].startDate, 'dd/mm/YY') : 'Not selected'}
                    </div>
                </div>
                <div className={classes.datepickerPreviewDate}>
                    <div className={classes.rangeDateIcon}>
                        <RangeDateIcon />
                    </div>
                    <span>{t('to.date')}</span>
                    <div className={classes.datepickerSelectedDate}>
                        {!!dateRange[0].endDate ? bareDateTimeFormat(dateRange[0].endDate, 'dd/mm/YY') : 'Not selected'}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CalendarWrapper;
